const CRAWLER_ORG_ID = "00000000-0000-0000-0000-000000000001";

interface CrawlerMeta {
  company_name?: string | null;
  company_logo_url?: string | null;
  external_apply_url?: string | null;
  source_name?: string | null;
  source_url?: string | null;
  source_type?: string | null;
  crawler_score?: number | null;
  [key: string]: unknown;
}

interface JobLike {
  org_id?: string;
  partner_orgs?: { name?: string | null; is_auto_created?: boolean | null; logo_url?: string | null } | null;
  crawler_metadata?: CrawlerMeta | CrawlerMeta[];
}

/** Safely extract the first crawler_metadata entry (handles array or object). */
export function getMeta(job: JobLike): CrawlerMeta | undefined {
  const m = job.crawler_metadata;
  return Array.isArray(m) ? m[0] : m;
}

export function isCrawlerJob(job: JobLike): boolean {
  if (job.org_id === CRAWLER_ORG_ID) return true;
  if (job.partner_orgs?.is_auto_created === true) return true;
  return false;
}

export function getCompanyName(job: JobLike): string {
  const meta = getMeta(job);
  if (meta?.company_name) return meta.company_name;
  if (job.partner_orgs?.is_auto_created && job.partner_orgs?.name) {
    return job.partner_orgs.name.replace(/\s*\(via Crawler\)\s*$/, "");
  }
  if (job.partner_orgs?.name) return job.partner_orgs.name;
  return "Empresa não informada";
}

export function getExternalApplyUrl(job: JobLike): string | null {
  return getMeta(job)?.external_apply_url || null;
}

export function isExternalJob(job: JobLike): boolean {
  return !!getExternalApplyUrl(job);
}

export function getSourceName(job: JobLike): string | null {
  return getMeta(job)?.source_name || null;
}

export function getSourceUrl(job: JobLike): string | null {
  return getMeta(job)?.source_url || null;
}

export function getSourceType(job: JobLike): string | null {
  return getMeta(job)?.source_type || null;
}

export function getCrawlerScore(job: JobLike): number | null {
  return getMeta(job)?.crawler_score ?? null;
}

const SOURCE_LABELS: Record<string, string> = {
  ciee: "CIEE",
  nube: "Nube",
  gupy: "Gupy",
  linkedin: "LinkedIn",
  indeed_brasil: "Indeed",
  adzuna: "Adzuna",
  greenhouse: "Greenhouse",
  lever: "Lever",
  workable: "Workable",
  google_jobs: "Google Jobs",
  espro: "Espro",
  isbet: "ISBET",
  catho: "Catho",
  infojobs: "InfoJobs",
  vagas_com: "Vagas.com",
  trabalha_brasil: "Trabalha Brasil",
  super_estagios: "Super Estágios",
  cia_estagios: "Cia de Estágios",
  jooble: "Jooble",
  emprega_brasil: "Emprega Brasil",
  programathor: "Programathor",
  geekhunter: "GeekHunter",
  remotar: "Remotar",
  walljobs: "WallJobs",
  ninety_nine_jobs: "99Jobs",
};

export function getSourceLabel(sourceName: string | null): string {
  if (!sourceName) return "Fonte desconhecida";
  return SOURCE_LABELS[sourceName] || sourceName;
}

export function getSourceTypeBadge(sourceType: string | null): { label: string; color: string } {
  switch (sourceType) {
    case "ats":
    case "ats_feed":
      return { label: "ATS", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" };
    case "specialized":
      return { label: "Especializado", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" };
    case "government":
      return { label: "Governo", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" };
    case "aggregator":
      return { label: "Agregador", color: "bg-muted text-muted-foreground" };
    default:
      return { label: "Externo", color: "bg-muted text-muted-foreground" };
  }
}

export function getCompanyLogoUrl(job: JobLike): string | null {
  const meta = getMeta(job);
  return job.partner_orgs?.logo_url || meta?.company_logo_url || null;
}

/** Append UTM parameters to an external URL so destination sites can see traffic from Corre Digital. */
export function withUtm(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set("utm_source", "corre-digital");
    u.searchParams.set("utm_medium", "projeto-social");
    u.searchParams.set("utm_campaign", "que-tal-parceria");
    u.searchParams.set("utm_term", "www.corredigital.ong.br");
    u.searchParams.set("utm_content", "venha-conhecer-a-gente");
    return u.toString();
  } catch {
    return url;
  }
}
